import { effect } from '@preact/signals';
import { render } from 'preact';
import {
  connect as connectGame,
  createGame,
  disconnect as disconnectGame,
  resize as resizeGame,
  start as startGame,
  stop as stopGame,
} from './game/Game';
import { captureError } from './state/error';
import { showSplash } from './state/splash';
import { App } from './ui/App';
import './index.css';

// ============================================================================
// Global Error Handlers
// ============================================================================

// Handle synchronous errors
window.onerror = (message, source, line, column, error) => {
  captureError({
    message: String(message),
    ...(error?.stack && { stack: error.stack }),
    ...(source && { source }),
    ...(line && { line }),
    ...(column && { column }),
  });
  // Don't suppress default console logging
  return false;
};

// Handle unhandled Promise rejections
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const error = event.reason;
  captureError({
    message: error?.message ?? String(error),
    stack: error?.stack,
  });
};

let resizeHandler: (() => void) | null = null;
let beforeUnloadHandler: (() => void) | null = null;
let appRoot: HTMLElement | null = null;
let contextMenuHandler: ((event: MouseEvent) => void) | null = null;

const init = async (): Promise<void> => {
  appRoot = document.getElementById('app');
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const uiOverlay = document.getElementById('ui-overlay');

  if (!appRoot) {
    throw new Error('App root element not found');
  }

  if (!canvas) {
    throw new Error('Canvas element not found');
  }

  if (!uiOverlay) {
    throw new Error('UI overlay element not found');
  }

  // Initialize game
  await createGame({ canvasElement: canvas });
  startGame();

  // Render Preact UI
  render(<App />, uiOverlay);

  // Handle window resize
  resizeHandler = () => {
    resizeGame({ width: window.innerWidth, height: window.innerHeight });
  };
  window.addEventListener('resize', resizeHandler);

  // Explicitly close the socket before a browser refresh/navigation.
  beforeUnloadHandler = () => {
    disconnectGame();
    stopGame();
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);

  contextMenuHandler = (event: MouseEvent) => {
    event.preventDefault();
  };
  appRoot.addEventListener('contextmenu', contextMenuHandler);

  // Connect to game server once splash is dismissed
  const serverUrl = import.meta.env.DEV ? 'ws://localhost:9001' : import.meta.env.VITE_SERVER_URL;

  if (serverUrl) {
    const dispose = effect(() => {
      if (!showSplash.value) {
        console.log(`Connecting to server at ${serverUrl}...`);
        connectGame({ serverUrl });
        dispose();
      }
    });
  }
};

init().catch(console.error);

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
    }
    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
    if (contextMenuHandler && appRoot) {
      appRoot.removeEventListener('contextmenu', contextMenuHandler);
    }
    disconnectGame();
    stopGame();
  });
}
